import { beautifyThePrice, isUndef } from '@lhb/func';
import { TrustSpotLibraryItem, TrustSpotLibraryResponse } from '@/common/api/locxxTrust';
import { getHeightLimitValue } from '../../../utils/heightLimit';

function normalizeString(value: any) {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizePositioningBrand(value: string | undefined) {
  const values = normalizeString(value).split(/[、,，]/);
  return values.reduce<string[]>((prev, item) => {
    const brandName = normalizeString(item);
    return brandName ? prev.concat(brandName) : prev;
  }, []);
}

function normalizeNumber(value: any) {
  if (value === '' || isUndef(value) || value === null) {
    return undefined;
  }
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : undefined;
}

function normalizeOptionText(value: any) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeCityIdList(cityIdList: any) {
  if (!Array.isArray(cityIdList)) {
    return [];
  }
  return cityIdList.reduce<number[]>((prev, currentItem) => {
    if (Array.isArray(currentItem)) {
      const targetValue = currentItem[currentItem.length - 1];
      if (isUndef(targetValue) || targetValue === '') {
        return prev;
      }
      const nextValue = Number(targetValue);
      return Number.isFinite(nextValue) ? prev.concat(nextValue) : prev;
    }
    if (isUndef(currentItem) || currentItem === '') {
      return prev;
    }
    const nextValue = Number(currentItem);
    return Number.isFinite(nextValue) ? prev.concat(nextValue) : prev;
  }, []);
}

function normalizeNumberList(values: any) {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.reduce<number[]>((prev, value) => {
    const nextValue = normalizeNumber(value);
    return isUndef(nextValue) ? prev : prev.concat(nextValue);
  }, []);
}

function normalizeStringList(values: any) {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.reduce<string[]>((prev, value) => {
    const nextValue = normalizeString(value);
    return nextValue ? prev.concat(nextValue) : prev;
  }, []);
}

export function normalizePointSearchValues(values: Record<string, any> = {}) {
  const keyword = normalizeString(values.keyword);
  const cityIdList = normalizeCityIdList(values.cityIdList);
  const locationTypes = Array.isArray(values.locationTypes) ? values.locationTypes.filter((item) => !isUndef(item) && item !== '') : [];
  const videoSourceFilterList = normalizeStringList(values.videoSourceFilterList);
  const spotFloorList = normalizeNumberList(values.spotFloorList);
  const indoorOrOutdoorId = normalizeNumber(values.indoorOrOutdoorId);
  const spotAreaMin = normalizeNumber(values.spotAreaMin);
  const spotAreaMax = normalizeNumber(values.spotAreaMax);
  const measuredDailyFlowType = normalizeNumber(values.measuredDailyFlowType);
  const cpmType = normalizeNumber(values.cpmType);
  const placePublicId = values.placePublicId;

  return {
    ...(keyword ? { keyword } : {}),
    ...(cityIdList.length ? { cityIdList } : {}),
    ...(locationTypes.length ? { spotPositionList: locationTypes } : {}),
    ...(videoSourceFilterList.length ? { videoSourceFilterList } : {}),
    ...(spotFloorList.length ? { spotFloorList } : {}),
    ...(indoorOrOutdoorId || indoorOrOutdoorId === 0 ? { indoorOrOutdoorId } : {}),
    ...(spotAreaMin || spotAreaMin === 0 ? { spotAreaMin } : {}),
    ...(spotAreaMax || spotAreaMax === 0 ? { spotAreaMax } : {}),
    ...(measuredDailyFlowType || measuredDailyFlowType === 0 ? { measuredDailyFlowType } : {}),
    ...(cpmType || cpmType === 0 ? { cpmType } : {}),
    ...(!isUndef(placePublicId) && placePublicId !== '' ? { placePublicId } : {}),
  };
}

export function mapTrustPointListItem(item: TrustSpotLibraryItem = {}) {
  return {
    spotId: item.spotId,
    tenantSpotId: item.spotId,
    tenantPlaceId: item.tenantPlaceId,
    modelClusterId: item.modelClusterId,
    spotName: item.spotName,
    placeName: item.placeName,
    cityName: item.cityName || '',
    city: item.cityName || '',
    spotPosition: item.spotPosition,
    spotFloor: item.spotFloor,
    indoorOrOutdoor: item.indoorOrOutdoor,
    spotArea: item.spotArea,
    heightLimit: item.heightLimit,
    measuredDailyFlow: item.measuredDailyFlow,
    coverUrl: item.coverUrl,
    positioningbrand: item.positioningbrand,
    dailyFlowSourceName: item.dailyFlowSourceName,
    videoSource: item.videoSource,
    videoSourceName: item.videoSourceName,
    dailyPublishPrice: item.retailDailyPrice,
    retailDailyPrice: item.retailDailyPrice,
    cpm: item.cpm,
  };
}

export function mapTrustPointListResponse(response: TrustSpotLibraryResponse = {}) {
  return {
    total: response.totalNum || 0,
    list: Array.isArray(response.objectList) ? response.objectList.map(mapTrustPointListItem) : [],
  };
}

export function formatPointPropertyOptions(options: any) {
  if (!Array.isArray(options)) {
    return [];
  }
  return options.reduce((prev: Array<{ value: string | number; label: string }>, currentItem: any) => {
    if (typeof currentItem === 'string') {
      const text = normalizeOptionText(currentItem);
      return text ? prev.concat({ value: text, label: text }) : prev;
    }
    if (!currentItem || typeof currentItem !== 'object') {
      return prev;
    }
    const value = isUndef(currentItem.value) ? currentItem.id : currentItem.value;
    const label = normalizeOptionText(currentItem.label) || normalizeOptionText(currentItem.name);
    if (isUndef(value) || !label) {
      return prev;
    }
    return prev.concat({ value, label });
  }, []);
}

export function getPointPropertyOptionSource(response: any) {
  if (Array.isArray(response)) {
    return response;
  }
  if (!response || typeof response !== 'object') {
    return [];
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.objectList)) {
    return response.objectList;
  }
  if (Array.isArray(response.records)) {
    return response.records;
  }
  return [];
}

export function formatPointNameAndPlace(record: Record<string, any> = {}) {
  const textList = [record.spotName, record.placeName].filter((item) => !!normalizeString(item));
  return textList.length ? textList.join(' / ') : '--';
}

export function getPointNameAndPlaceLines(record: Record<string, any> = {}) {
  const spotName = normalizeString(record.spotName);
  const placeName = normalizeString(record.placeName);
  if (spotName && placeName) {
    return {
      primary: spotName,
      secondary: placeName,
    };
  }
  const singleLineText = spotName || placeName || '--';
  return {
    primary: singleLineText,
    secondary: '',
  };
}

export function formatPointAreaAndHeight(record: Record<string, any> = {}) {
  const areaText = record.spotArea || record.spotArea === 0 ? `${beautifyThePrice(record.spotArea, ',', 'auto')}㎡` : '';
  const heightValue = getHeightLimitValue(record.heightLimit);
  const heightText = heightValue ? `${heightValue}m` : '';
  if (areaText && heightText) {
    return `${areaText} / ${heightText}`;
  }
  return areaText || heightText || '--';
}

export function getPointAreaAndHeightLines(record: Record<string, any> = {}) {
  const areaText = record.spotArea || record.spotArea === 0 ? `${beautifyThePrice(record.spotArea, ',', 'auto')}㎡` : '';
  const heightValue = getHeightLimitValue(record.heightLimit);
  const heightText = heightValue ? `${heightValue}m` : '';
  if (areaText && heightText) {
    return {
      primary: areaText,
      secondary: `限高 ${heightText}`,
    };
  }
  return {
    primary: areaText || heightText || '--',
    secondary: '',
  };
}
